import { sortByName } from '@pubstack/common/src/sort'
import { StackContext } from '@pubstack/common/src/stackContext'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from '../api-client'

export const CONTEXTS_QUERY_KEYS = ['adm', 'contexts']

/**
 * The message for the name field, when the failure is about the name.
 *
 * `useMutation` rejects with the response body itself rather than an AxiosError, so there is no `response` to
 * read: the reason lives in `description`, and `message` is only the status label. A name already taken is the
 * one refusal the author can act on, and the field it belongs to is the one the API names.
 */
export const contextNameErrorMessage = (error: unknown): string | undefined => {
  const description = (error as { description?: { field?: string; message?: string } })?.description
  return description?.field === 'name' ? description.message : undefined
}
const url = '/adm/contexts'
//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/**
 * `enabled` asks the API for the live contexts only; omitting it asks for the archived ones too, which is
 * what the archived page and the stack views that resolve a name off an id want. Same spelling as the ad
 * units hook, and the same reason for reading it off the server rather than filtering here: the everyday
 * list and the archived page must not be able to disagree about which context is which.
 */
export const useContexts = (options: { withStacksUse?: boolean; enabled?: boolean } = {}) => {
  const urlWithSearchParams = buildUrlWithSearchParams(url, options)
  const query = useQuery<StackContext[]>({
    url: urlWithSearchParams,
    keys: [...CONTEXTS_QUERY_KEYS, options],
    select: sortByName,
  })
  return { ...query, data: query.data || [] }
}

export const useStackContext = (id: string, enabled: boolean = !!id) => {
  return useQuery<StackContext>({
    url: `${url}/${id}`,
    keys: [...CONTEXTS_QUERY_KEYS, id],
    enabled,
  })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateContext = (toasts: MutationToasts = {}) => {
  return useMutation<StackContext, StackContext>({
    method: 'post',
    url,
    collectionKeys: CONTEXTS_QUERY_KEYS,
    ...toasts,
  })
}

/**
 * The context id is only known at call time (archiving is triggered from an arbitrary table row),
 * hence the url is built from the mutation variables.
 */
export const useUpdateContext = (toasts: MutationToasts = {}) => {
  return useMutation<StackContext, StackContext>({
    method: 'put',
    url: (context) => `${url}/${context.id}`,
    collectionKeys: CONTEXTS_QUERY_KEYS,
    ...toasts,
  })
}
